import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {
	BOTTOM_CHANNEL_HANDLE,
	COLOR_FRAME_PELMET,
	DEFAULT_UNCOLORED_COLOR,
	FRAME_COLORS,
	INTERIOR_COLOR_FRAME_PELMET,
	INTERIOR_END_CAPS,
	INTERIOR_FRAME_TOP_STYLE,
	SPLINE_MESH,
	STORAGE_NAMES,
	VIEW_TYPES,
} from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import {
	BlindData,
	BottomChannel,
	FrameColor,
	FrameColorConfig,
	Frames,
	IBlindSelect,
	MaterialType,
	SectionKeysEnum,
} from '@root/app.interfaces';
import { VariantService } from '@core/services/variant/variant.service';
import * as _ from 'lodash';
import { EngineService } from '@core/services/engine/engine.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { Subscription } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';

@Component({
	selector: 'app-frame-color',
	templateUrl: './frame-color.component.html',
	styleUrls: ['./frame-color.component.scss', './frame-color.responsive.scss'],
})
export class FrameColorComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('frameColorItem') frameColorItem: ElementRef<HTMLElement>;

	@Output() frameData: EventEmitter<{ frames: Frames }> = new EventEmitter();
	@Input() blindId: number;

	protected readonly frameColors = FRAME_COLORS;

	sessionText = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);
	sessionConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);
	blindType: 'interior' | 'outdoor' = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
	isDesignType: boolean = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;

	frames: FrameColorConfig[];
	framesData: { frames: Frames };
	frameColorObject = {
		color: '',
		id: 0,
		name: '',
		is_custom: false,
	};

	frameColor: string;
	otherColor: string;
	defaultColor: string;
	customColor: string;
	materialType: string;

	optionals: BottomChannel[] = [];

	random = Math.floor(10000 + Math.random() * 1000);

	isShop = this.variant.isShop();
	dontSetColour = this.variant.isShop();

	blindSelectSubscription: Subscription;
	materialChangeSubscription: Subscription;

	constructor(
		private readonly variant: VariantService,
		private readonly engineService: EngineService,
		private readonly blindStateService: BlindStateService,
		private readonly saveSessionBlind: SaveSessionBlindsService,
		private readonly sessionStorageService: SessionStorageService,
		private readonly changeDetection: ChangeDetectorRef,
		private readonly shareDataService: ShareService,
	) {}

	static activeClassHandler(container: HTMLElement, selector?: HTMLElement): void {
		for (const item of container.children) {
			item.classList.remove('active');
		}

		if (selector) {
			selector.classList.add('active');
		}
	}

	ngOnInit() {
		this.getFrameFromStorage();

		this.frames = this.sessionConfig?.[this.blindType].frame.frame_color;
		this.defaultColor = this.sessionConfig?.[this.blindType].frame.frame_color.filter((item) => item.is_default === true)[0].color;

		if (this.blindType === 'interior') {
			this.optionals = this.sessionConfig?.[this.blindType].frame.optionals;
		}

		this.blindSelectSubscription = this.shareDataService.getBlindSelect.subscribe(this.setBlindSelectId.bind(this));
		this.materialChangeSubscription = this.shareDataService.getMaterialColorChanged.subscribe(this.getMaterialType.bind(this));
	}

	ngOnChanges(changes: SimpleChanges): void {
		const getCurrentBLindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);

		if (changes.blindId.currentValue === getCurrentBLindId) {
			setTimeout(this.setDefaultValue.bind(this));
		}
	}

	ngOnDestroy() {
		this.blindSelectSubscription.unsubscribe();
		this.materialChangeSubscription.unsubscribe();
	}

	getFrameFromStorage(id?: number | string): void {
		const blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const blinds = this.sessionStorageService.getBlindItemById(id || this.blindId, STORAGE_NAMES.zip_blind_data);

		if (blinds) {
			for (const blind of blinds) {
				if (blind.setup && blind.setup.frames && id === this.blindId) {
					const { frame_color } = blind.setup.frames;
					this.dontSetColour = frame_color ? !frame_color.configured : this.dontSetColour;

					this.framesData = { frames: { ...blind.setup.frames } };

					this.frameColorObject = {
						is_custom: false,
						...frame_color,
					};

					if (blindType === 'interior') {
						const isChecked = !_.isEmpty(blind.setup.frames.optionals);
						this.optionals = isChecked ? _.clone(blind.setup.frames.optionals) : this.sessionConfig?.[blindType].frame.optionals;

						if (this.optionals?.length) {
							this.optionals[0].is_checked = isChecked;
						}

						this.setBottomChannel(isChecked, 'storage');
					}
				}
			}
		}
	}

	onSelectColor<T extends { event?: Event; is_extra_fee?: boolean } & FrameColor>(
		data: T,
		saveChangeTime = true,
		buttonPress = false,
	): void {
		if (buttonPress) {
			this.dontSetColour = false;
		}

		this.getInteriorTopStyle();
		this.frameColor = data.color;

		if (this.isOtherColor(data.name)) {
			this.otherColor = data.color;
		}

		const frameColorChanged = {
			frame_color: {
				id: data.id,
				name: data.name,
				color: data.color,
				color_name: data.color_name,
				configured: !this.dontSetColour,
				is_extra_fee: data.is_extra_fee,
			},
		};
		const framesData = {
			frames: { ...this.framesData.frames, ...frameColorChanged },
		};
		this.framesData = {
			frames: {
				...this.framesData.frames,
				...frameColorChanged,
			},
		};

		this.setModelColor(data.color);
		this.frameColorObject.is_custom = !_.isEmpty(data.is_custom);
		this.frameColorObject = { ...this.frameColorObject, ...data };
		this.saveBlindSetup(framesData, saveChangeTime);
	}

	onInputCustomColor(event: Event, color: string): void {
		const framesData = this.framesData.frames;
		const isChecked = (event.target as HTMLInputElement).checked;
		const lastSavedColor = framesData.frame_color.last_saved_color
			? framesData.frame_color.last_saved_color
			: !framesData.frame_color.is_custom
				? framesData.frame_color.color
				: this.defaultColor;
		color = color.trim();

		if (!_.isEmpty(this.framesData.frames) && !_.isEmpty(color)) {
			const colorProperty = {
				color,
				color_name: color,
				last_saved_color: lastSavedColor,
				id: 0,
				name: 'Custom',
				is_custom: true,
			};

			this.customColor = color;
			this.frameColorObject = colorProperty;
			framesData.frame_color = this.frameColorObject;
			this.frameColor = DEFAULT_UNCOLORED_COLOR;
			FrameColorComponent.activeClassHandler(this.frameColorItem.nativeElement, null);
			this.setModelColor(DEFAULT_UNCOLORED_COLOR);
		}

		if (_.isEmpty(color) || (!isChecked && event.type === 'change')) {
			const elem = this.frameColorItem.nativeElement.querySelectorAll(`[data-color="${lastSavedColor}"]`)[0];
			this.frameColorObject.is_custom = false;

			if (elem.classList.contains('other')) {
				const otherColor = {
					id: 99,
					name: 'Other',
					color: lastSavedColor,
				};
				elem.classList.add('active');
				this.onSelectColor(otherColor);
			} else {
				elem.dispatchEvent(new Event('click'));
			}
		}

		if (this.frameColorObject.is_custom && (event.type === 'blur' || event.type === 'change' || event.type === 'focus')) {
			this.saveBlindSetup();
		}
	}

	setModelColor(color): void {
		const getBlindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const getBlindSetup = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		let meshes: string[];

		if (getBlindType === 'outdoor') {
			meshes = this.materialType !== MaterialType.ClearPVC ? COLOR_FRAME_PELMET : [...COLOR_FRAME_PELMET, ...SPLINE_MESH];
		} else {
			meshes = INTERIOR_COLOR_FRAME_PELMET;

			if (getBlindSetup.setup?.size) {
				const width = getBlindSetup.setup.size['width'];
				const height = getBlindSetup.setup.size['height'];
				const topStyleId = INTERIOR_FRAME_TOP_STYLE.reduce((acc, x) => (!acc && width > x.width && height > x.height ? x.id : acc), '');
				const topStyle = this.sessionConfig[getBlindType].frame.top_style.filter((el) => el.id === topStyleId)[0];

				meshes = topStyle.id === INTERIOR_FRAME_TOP_STYLE[0].id ? [...meshes, ...INTERIOR_END_CAPS] : meshes;
			}
		}

		for (const meshId of meshes) {
			this.engineService.setColor(meshId, color, 1, 'frame', this.blindType);
		}
	}

	saveBlindSetup(framesData = this.framesData, saveChangeTime = true): void {
		const blind: BlindData = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const { frame_color, optionals } = framesData.frames;
		framesData.frames = {
			...blind.setup.frames,
			frame_color,
			optionals,
		};
		this.frameData.emit(framesData);
		this.sessionStorageService.setBlindSetupDataById(this.blindId, framesData, STORAGE_NAMES.zip_blind_data, saveChangeTime);
		this.saveSessionBlind.PutStorageDataToServer();

		if (!this.dontSetColour) {
			this.blindStateService.markConfigured(this.blindId, SectionKeysEnum.FrameColor);
		}
	}

	setDefaultValue(): void {
		const getBlindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const blind = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const frameConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config)[getBlindType].frame;
		const blindFrames = blind.setup.frames;

		if (_.isEmpty(this.framesData?.frames)) {
			this.framesData = {
				frames: {
					frame_color: {
						id: null,
						name: null,
						color: null,
						color_name: null,
					},
					top_style: {
						id: null,
						name: null,
					},
					bottom_bar: {
						id: null,
						name: null,
					},
					optionals: [],
				},
			};
		}

		if (typeof blindFrames?.frame_color?.id !== 'number') {
			for (const color of frameConfig.frame_color) {
				if (color.is_default) {
					this.defaultColor = color.color;
					this.onSelectColor(color, false);
				}
			}
		}

		if (!this.isDesignType && !_.isEmpty(blindFrames)) {
			this.setBlindSelectId({ id: this.blindId });
		}
	}

	setBlindSelectId(data: IBlindSelect): void {
		this.getFrameFromStorage(data.id);
		this.blindType = this.sessionStorageService.getBlindItemById(data?.id, STORAGE_NAMES.zip_blind_data)[0].type;
		this.frames = this.sessionConfig[this.blindType].frame.frame_color;

		if (data.status === 'new') {
			this.frameColorObject.color = this.defaultColor;
		}

		if (data.id === this.blindId) {
			const frameColor = this.frameColorObject.is_custom
				? DEFAULT_UNCOLORED_COLOR
				: _.isEmpty(this.frameColorObject.color)
					? this.defaultColor
					: this.frameColorObject.color;
			this.customColor = this.frameColorObject.is_custom ? this.frameColorObject.color : '';

			this.setModelColor(frameColor);

			if (!_.isEmpty(this.frameColorObject.name) && this.isOtherColor(this.frameColorObject.name)) {
				this.frameColor = frameColor;
				this.otherColor = frameColor;
			}
		}

		this.changeDetection.markForCheck();
	}

	getMaterialType(colorProperty): void {
		const getBlindSetup = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const isCurrentBlind = colorProperty.blind_id === this.blindId;
		const frameColorData = getBlindSetup?.setup?.frames?.frame_color;

		this.materialType = colorProperty.type;
		this.frameColor = frameColorData?.color || this.frameColor;
		this.otherColor = this.isOtherColor(frameColorData?.name)
			? frameColorData?.color
			: FRAME_COLORS.map((item) => item.color).includes(frameColorData?.last_saved_color)
				? frameColorData?.last_saved_color
				: this.otherColor;

		// PVC type
		if (colorProperty.type === MaterialType.ClearPVC && this.frameColor && isCurrentBlind) {
			this.setModelColor(this.frameColor);
		}
	}

	onSelectBottomChannel(data: BottomChannel, state: boolean, event: Event): void {
		this.getInteriorTopStyle();
		this.setBottomChannel(state, event.type, data.name);
		this.saveBlindSetup();
	}

	setBottomChannel(state: boolean, type: string, description: string = ''): void {
		const blindData = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const mounting = blindData?.setup?.mounting?.name?.toLowerCase();
		const isFaceFix = mounting === 'face fix';
		const bottomChannel = BOTTOM_CHANNEL_HANDLE.filter((name) => !state || name.toLowerCase().includes(mounting));

		if (bottomChannel) {
			this.engineService.setBottomChannel({
				meshes: bottomChannel,
				state,
				type,
				description,
			});
		}

		if (isFaceFix) {
			this.engineService.getInteriorFaceFixStopper(!state);
		}
	}

	getInteriorTopStyle() {
		const top_style = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0].setup.frames?.top_style;

		if (this.framesData?.frames && top_style) {
			this.framesData = { frames: { ...this.framesData.frames, top_style } };
		}
	}

	onSubmitCheckboxOptions(event: Event): void {
		const currentOption = event.currentTarget as HTMLElement;
		const isChecked = (currentOption.previousElementSibling as HTMLInputElement)?.checked;
		const optionID = currentOption.id;
		const { ...getCurrentData } = this.optionals.find((x) => x.id === +optionID);

		if (!isChecked && !this.framesData.frames.optionals.length) {
			this.framesData.frames.optionals.push(getCurrentData);
		} else {
			this.framesData.frames.optionals.pop();
		}

		if (optionID === '1') {
			this.onSelectBottomChannel(getCurrentData, !isChecked, event);
		}
	}

	private isOtherColor(colorName: string): boolean {
		return colorName?.toLowerCase() === 'other';
	}
}
